// M69e — loads admin taxonomy overrides once at boot (and on window focus) into
// the module-level override store the tag accessors consult. No React Query in
// this app, so this is a plain supabase fetch + a context `version`.
//
// Propagation without a remount: the provider bumps `version` only when the
// fetched rows actually change; <AppShell> (and <AdminTagsPage>) read the
// version, so a change re-renders the layout → <Outlet/> → route pages → chips
// re-read the store. Component state is preserved (re-render, not remount).
//
// Non-blocking boot: children render immediately; a failed/empty fetch just
// leaves the code maps in place (resolvers fall back). A brief code-default
// flash before the first load resolves is accepted.
//
// Consistency across clients: refetch on window focus + on reload (edits are
// rare; no realtime subscription).

import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { supabase } from '../lib/supabase';
import { setTaxonomyOverrides, type TaxonomyOverrideRow } from '../lib/taxonomyOverrides';

interface TaxonomyOverridesCtx {
  /** Bumps whenever the override set changes — read it to re-render on change. */
  version: number;
  /** Re-fetch now (e.g. an admin just saved an override). */
  refetch: () => void;
}

const Ctx = createContext<TaxonomyOverridesCtx>({ version: 0, refetch: () => {} });

/** Read the current override version (referencing it subscribes to changes). */
// eslint-disable-next-line react-refresh/only-export-components
export function useTaxonomyOverridesVersion(): TaxonomyOverridesCtx {
  return useContext(Ctx);
}

export function TaxonomyOverridesProvider({ children }: { children: ReactNode }) {
  const [version, setVersion] = useState(0);
  const lastJson = useRef<string>('');

  const load = useCallback(async () => {
    const { data, error } = await supabase
      .from('taxonomy_overrides')
      .select('family,value,label,color_light,color_dark,hidden,source');
    if (error || !data) return; // non-blocking: keep the code maps
    const rows = data as TaxonomyOverrideRow[];
    const json = JSON.stringify(rows);
    if (json === lastJson.current) return; // unchanged → no re-render
    lastJson.current = json;
    setTaxonomyOverrides(rows);
    setVersion((v) => v + 1);
  }, []);

  useEffect(() => {
    void load();
    const onFocus = () => void load();
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onFocus);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onFocus);
    };
  }, [load]);

  return <Ctx.Provider value={{ version, refetch: load }}>{children}</Ctx.Provider>;
}
